import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { Subscription } from 'rxjs';

const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage';
const CHECK_INTERVAL_MS = 2000;

/**
 * Watches the Okta token lifecycle and logs the user out once the session is no
 * longer valid.
 *
 * There are two detection paths, because the first one is unreliable inside the
 * Amazon Connect (CCP) iframe:
 *
 *   1. Store-emptied path: okta-auth-js's TokenManager normally removes expired
 *      tokens, leaving an empty `okta-token-storage`. We log out when the store
 *      goes from populated to empty.
 *
 *   2. Expiry-time path (the fix for the Connect iframe): the TokenManager
 *      relies on per-token `setTimeout` timers to emit `expired` and clear the
 *      store, and on a hidden `/authorize` iframe to silently auto-renew. When
 *      Hestia runs inside the Amazon Connect iframe, background timers are
 *      throttled/frozen and the hidden-iframe renew is blocked by third-party
 *      framing/cookie restrictions. The store therefore never empties, so the
 *      store-emptied path alone would keep the user signed in forever. To cover
 *      this, every poll tick also inspects each token's own `expiresAt` and
 *      forces logout once the tokens are actually expired, regardless of
 *      whether okta's internal timers fired.
 *
 * Logout is triggered only once the user has actually held tokens, so users who
 * have not signed in yet (login / third-party / public pages, where the store
 * is legitimately empty) are never bounced to /login.
 */
@Injectable({ providedIn: 'root' })
export class OktaSessionService implements OnDestroy {
    private started = false;
    private loggingOut = false;
    private hadTokens = false;
    private intervalId?: ReturnType<typeof setInterval>;
    private routerSubscription?: Subscription;

    // Tracks the most recent navigation target URL so we never read the
    // potentially stale this.router.url during an in-progress navigation.
    // Initialised to the current URL and kept up to date via NavigationStart.
    private currentUrl: string = '/';

    constructor(
        @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
        private router: Router
    ) {
        // Keep currentUrl in sync with every navigation attempt, not just
        // completed ones. This prevents the race condition where router.url
        // still reflects the previous route while a navigation to /login is
        // already in flight.
        this.routerSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.currentUrl = event.url;
            }
        });
    }

    /** Call once, after the app boots, to begin watching the token store. */
    start(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        // Seed currentUrl from the live router in case NavigationStart has
        // already fired before start() is called.
        this.currentUrl = this.router.url;

        // Check now, on an interval (same-tab expiry/removal), and on cross-tab
        // storage changes.
        this.checkTokenStorage();
        this.intervalId = setInterval(() => this.checkTokenStorage(), CHECK_INTERVAL_MS);
        window.addEventListener('storage', this.onStorageEvent);
    }

    /** Stop watching (listeners live for the app's lifetime, so rarely needed). */
    stop(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
        window.removeEventListener('storage', this.onStorageEvent);
        this.started = false;
    }

    ngOnDestroy(): void {
        this.stop();
        this.routerSubscription?.unsubscribe();
    }

    private onStorageEvent = (event: StorageEvent): void => {
        if (event.key === OKTA_TOKEN_STORAGE_KEY) {
            this.checkTokenStorage();
        }
    };

    private checkTokenStorage(): void {
        if (!this.isTokenStorageEmpty()) {
            // Tokens are present - remember it so we can detect their removal.
            this.hadTokens = true;

            // Inside the Amazon Connect iframe, okta-auth-js's background
            // expiry timers are throttled and silent renew is blocked, so the
            // store is never emptied on expiry. Proactively check the tokens'
            // own expiry time and log out when they have actually expired.
            if (this.areStoredTokensExpired()) {
                this.handleExpiredSession();
            }
            return;
        }

        // Store is empty. Only log out if the user actually had tokens before.
        if (this.hadTokens) {
            this.handleExpiredSession();
        }
    }

    /**
     * Reads the persisted token store and returns true when every stored token
     * is past its `expiresAt`. Returns false when the store is empty/unparseable
     * (that case is handled by the store-emptied path) or when any token is
     * still valid.
     *
     * `expiresAt` is stored by okta-auth-js in epoch SECONDS.
     */
    private areStoredTokensExpired(): boolean {
        const raw = localStorage.getItem(OKTA_TOKEN_STORAGE_KEY);
        if (!raw) {
            return false;
        }

        try {
            const parsed = JSON.parse(raw);
            const tokens = Object.values(parsed ?? {}).filter(
                (t): t is { expiresAt?: number } => !!t && typeof t === 'object'
            );

            const tokensWithExpiry = tokens.filter((t) => typeof t.expiresAt === 'number');
            if (tokensWithExpiry.length === 0) {
                return false;
            }

            const nowSeconds = Math.floor(Date.now() / 1000);
            // Expired only when none of the tokens are still valid.
            return tokensWithExpiry.every((t) => (t.expiresAt as number) <= nowSeconds);
        } catch {
            // Unparseable store is treated as empty by isTokenStorageEmpty();
            // don't force a logout from here.
            return false;
        }
    }

    /** True when okta-token-storage is missing, blank, or holds no tokens. */
    private isTokenStorageEmpty(): boolean {
        const raw = localStorage.getItem(OKTA_TOKEN_STORAGE_KEY);
        if (!raw) {
            return true; // missing / null / empty string
        }

        try {
            const parsed = JSON.parse(raw);
            return !parsed || Object.keys(parsed).length === 0; // "{}" -> empty
        } catch {
            return true; // unparseable -> treat as empty
        }
    }

    private async handleExpiredSession(): Promise<void> {
        // Use currentUrl (updated on NavigationStart) instead of router.url so
        // we correctly detect an in-progress navigation to /login that hasn't
        // committed yet — router.url is only updated after NavigationEnd.
        if (this.loggingOut || this.currentUrl.startsWith('/login')) {
            return;
        }
        this.loggingOut = true;

        // Make sure stale tokens can't keep the user "signed in" on the next
        // tick. Inside the Connect iframe okta's own timers may not have cleared
        // these, so clear them explicitly before redirecting.
        try {
            this.oktaAuth.tokenManager.clear();
        } catch {
            localStorage.removeItem(OKTA_TOKEN_STORAGE_KEY);
        }

        if (this.isInIframe()) {
            // When embedded in the Amazon Connect CCP iframe, okta's redirect
            // based signOut would navigate the iframe (not the agent's top
            // window) and is likely blocked by framing restrictions. Drive the
            // top-level window to the login page instead.
            this.redirectTopToLogin();
            return;
        }

        try {
            // Ends the Okta session and redirects to the post-logout URI.
            await this.oktaAuth.signOut();
        } catch (error) {
            console.error('Okta signOut failed, redirecting to login:', error);
            // Reset the flag before navigating so that if this fallback
            // navigation itself fails (or is cancelled), a subsequent poll tick
            // can attempt logout again rather than being permanently blocked.
            this.loggingOut = false;
            this.router.navigate(['/login']);
        }
    }

    /** True when Hestia is running inside an iframe (e.g. Amazon Connect CCP). */
    private isInIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch {
            // A cross-origin parent throws on access, which itself means we are
            // framed.
            return true;
        }
    }

    /**
     * Navigates the outermost browser window to the login page. Falls back to
     * the Angular router when the top window can't be reached (cross-origin).
     */
    private redirectTopToLogin(): void {
        const loginUrl = `${window.location.origin}/login`;
        try {
            if (window.top) {
                window.top.location.href = loginUrl;
                return;
            }
        } catch {
            // Cross-origin top window: fall through to in-frame navigation.
        }
        this.router.navigate(['/login']);
    }
}